/**
 * Discharge letter builder — renders the inline preview with editable
 * fields and wires it together with the PDF via `withDocumentEditor`.
 */
import { Input } from '@/frontend/components/ui/input'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { DischargeLetterPdf } from '@/frontend/features/printing/documents/discharge/discharge-template.print'
import type {
  DischargeLetterConfig,
  DischargeLetterFields,
} from '@/frontend/features/printing/documents/discharge/discharge-template.types'
import { defaultDischargeLetterFields } from '@/frontend/features/printing/documents/discharge/discharge-template.types'
import {
  activityText,
  anaesthesiaText,
  BATHING_TEXT,
  closingText,
  FOLLOW_UP_WHATSAPP,
  foodWaterText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  monitoringText,
  OUT_OF_HOURS,
  painMedIntroText,
  THINGS_TO_NOTICE,
  woundPhotoText,
} from '@/frontend/features/printing/documents/discharge/shared'
import type {
  DocumentContext,
  DocumentFormProps,
} from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function DischargeLetterForm({
  config,
  context,
  value,
  onChange,
}: DocumentFormProps<DischargeLetterFields> & { config: DischargeLetterConfig }) {
  const { patient, clinic } = context
  const name = patient.name

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      {config.title && <h1 className="mb-4 text-lg font-bold">{config.title}</h1>}

      {/* Procedure line */}
      {config.procedureField ? (
        <p className="mb-4 flex flex-wrap items-center gap-2">
          <strong>Procedure:</strong> {config.procedureText(name)}
          <Input
            className="w-64"
            value={value.procedureName}
            onChange={(e) => onChange({ ...value, procedureName: e.target.value })}
            placeholder="procedure name"
          />
        </p>
      ) : (
        <p>
          <strong>Procedure:</strong> {config.procedureText(name)}
        </p>
      )}

      <p className="mt-3">
        <strong>Activity:</strong> {activityText(name)}
      </p>
      <p className="mt-3">
        <strong>Anaesthesia:</strong> {anaesthesiaText(name)}
      </p>
      <p className="mt-3">
        <strong>Food/Water:</strong> {foodWaterText(name)}
      </p>
      <p className="mt-3">
        <strong>Bathing:</strong> {BATHING_TEXT}
      </p>
      <p className="mt-3">
        <strong>Monitoring:</strong> {monitoringText(name)}
      </p>

      <h2 className="text-headline-md">Medication:</h2>
      <p className="mt-2">{medicationText(name)}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span>{painMedIntroText(name)}</span>
        <Input
          className="w-64"
          value={value.painMedication}
          onChange={(e) => onChange({ ...value, painMedication: e.target.value })}
        />
      </div>
      <p className="mt-2">{MEDICATION_SIDE_EFFECTS}</p>

      <h2 className="text-headline-md">Follow up appointments:</h2>
      <ul className="mt-2 list-disc space-y-2 pl-6">
        <li>{FOLLOW_UP_WHATSAPP}</li>
        <li>
          <span>{woundPhotoText(name)}</span>
          <div className="mt-1 flex items-center gap-2">
            <Input
              className="w-48"
              value={value.woundCheckDate1}
              onChange={(e) => onChange({ ...value, woundCheckDate1: e.target.value })}
              placeholder="Date 1"
            />
            <span>&</span>
            <Input
              className="w-48"
              value={value.woundCheckDate2}
              onChange={(e) => onChange({ ...value, woundCheckDate2: e.target.value })}
              placeholder="Date 2"
            />
          </div>
        </li>
      </ul>

      <h2 className="text-headline-md">Other things you may notice:</h2>
      <ul className="mt-2 list-disc space-y-2 pl-6">
        {THINGS_TO_NOTICE.map((item) => (
          <li key={item.slice(0, 40)}>{item}</li>
        ))}
      </ul>

      <h2 className="text-headline-md">Out of hours contact:</h2>
      <p className="mt-2">{OUT_OF_HOURS}</p>

      <p className="mt-4">{closingText(name)}</p>
      <p>Snoots Vet, North London</p>
    </DocumentPreview>
  )
}

/** Build a discharge letter from a procedure-specific config. */
export function buildDischargeLetter(config: DischargeLetterConfig) {
  return withDocumentEditor({
    id: config.id,
    label: config.label,
    defaultFields: { ...defaultDischargeLetterFields },
    Preview: (props: DocumentFormProps<DischargeLetterFields>) => (
      <DischargeLetterForm {...props} config={config} />
    ),
    Pdf: ({ context, fields }: { context: DocumentContext; fields: DischargeLetterFields }) => (
      <DischargeLetterPdf config={config} context={context} fields={fields} />
    ),
  })
}
